import type { SceneObject } from "./SceneHost";
import { isStickyObject, isTextObject, patchTextStyleAndFit } from "./textObject";
import { isFrameObject } from "./frame/frameContext";

/**
 * Копирование стиля, как в Miro: Ctrl+Alt+C берёт оформление выделенного
 * объекта, Ctrl+Alt+V кладёт его на выделенные. Переносится только то,
 * что у объекта-получателя есть: у линии — цвет и толщина, у надписи —
 * шрифт, размер, цвет и фон, у фигуры — заливка, обводка и шрифт подписи.
 *
 * Хранится в памяти вкладки (и в sessionStorage — на случай перезагрузки):
 * это буфер стиля, а не буфер обмена — объекты Ctrl+C/Ctrl+V не трогает.
 */

type StyleBag = Record<string, unknown>;

const STORAGE_KEY = "wb-copied-style";

const TEXT_KEYS = [
  "color",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "textDecoration",
  "textAlign",
  "lineHeight",
] as const;

const STROKE_KEYS = [
  "stroke",
  "strokeWidth",
  "strokeOpacity",
  "strokeLinecap",
  "strokeLinejoin",
  "strokeDash",
] as const;

const FILL_KEYS = ["fill", "fillOpacity"] as const;

/** Объекты из линий: перо, прямая, стрелка, маркер. Заливка им не нужна. */
const LINE_TYPES = new Set(["path", "line", "arrow"]);
/** Фигуры: заливка, обводка и подпись внутри. */
const SHAPE_TYPES = new Set(["rect", "ellipse", "triangle", "diamond", "star", "geom"]);

let copied: StyleBag | null = null;

function readStored(): StyleBag | null {
  if (copied) return copied;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") copied = parsed as StyleBag;
  } catch {
    /* хранилище недоступно — живём с памятью вкладки */
  }
  return copied;
}

function styleOf(obj: SceneObject): StyleBag {
  return obj.style && typeof obj.style === "object" ? { ...(obj.style as StyleBag) } : {};
}

/** Какие поля стиля имеют смысл у этого объекта. */
function keysFor(obj: SceneObject): readonly string[] {
  if (isTextObject(obj)) return [...TEXT_KEYS, ...FILL_KEYS];
  if (isStickyObject(obj)) return [...TEXT_KEYS, "backgroundColor"];
  if (isFrameObject(obj)) return FILL_KEYS;
  if (LINE_TYPES.has(obj.type)) return STROKE_KEYS;
  if (SHAPE_TYPES.has(obj.type)) return [...STROKE_KEYS, ...FILL_KEYS, ...TEXT_KEYS];
  return [];
}

/** Запомнить стиль объекта. false — у объекта нечего копировать. */
export function copyObjectStyle(obj: SceneObject): boolean {
  const style = styleOf(obj);
  const bag: StyleBag = {};
  for (const key of keysFor(obj)) {
    if (style[key] !== undefined) bag[key] = style[key];
  }
  // У стикера цвет живёт в старом поле color — переносим его как фон.
  if (isStickyObject(obj) && bag.backgroundColor === undefined) {
    const legacy = (obj as { color?: unknown }).color;
    if (typeof legacy === "string") bag.backgroundColor = legacy;
  }
  // Цвет линии и цвет букв для человека — один «цвет»: стиль линии,
  // положенный на надпись, красит буквы.
  if (LINE_TYPES.has(obj.type) && typeof bag.stroke === "string") bag.color = bag.stroke;
  if (!Object.keys(bag).length) return false;
  copied = bag;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bag));
  } catch {
    /* не страшно */
  }
  return true;
}

export function hasCopiedStyle(): boolean {
  return Boolean(readStored());
}

/** Патч, который кладёт скопированный стиль на объект; null — нечего класть. */
export function pasteStylePatch(obj: SceneObject): Record<string, unknown> | null {
  const bag = readStored();
  if (!bag || obj.locked) return null;
  const allowed = keysFor(obj);
  const style: StyleBag = {};
  for (const key of allowed) {
    if (bag[key] !== undefined) style[key] = bag[key];
  }
  // Линия получает цвет от надписи: цвет букв становится цветом линии.
  if (LINE_TYPES.has(obj.type) && style.stroke === undefined && typeof bag.color === "string") {
    style.stroke = bag.color;
  }
  if (isStickyObject(obj) && style.backgroundColor === undefined && typeof bag.fill === "string" && bag.fill !== "none") {
    style.backgroundColor = bag.fill;
  }
  if (!Object.keys(style).length) return null;
  if (isTextObject(obj)) return patchTextStyleAndFit(obj, style);
  return { style };
}

type StyleTargetScene = {
  findObject(id: string): SceneObject | null;
  commitBatchPatches(
    patches: { id: string; patch: Record<string, unknown> }[],
    clientOpId: string
  ): Promise<void>;
};

/** Скопировать стиль первого выделенного. Текст подсказки для человека. */
export function copyStyleFromSelection(scene: StyleTargetScene | null, ids: string[]): string {
  const obj = ids.length ? scene?.findObject(ids[0]!) : null;
  if (!obj) return "Выделите объект, чей стиль скопировать";
  return copyObjectStyle(obj)
    ? "Стиль скопирован. Выделите другие объекты и нажмите Ctrl+Alt+V"
    : "У этого объекта нет стиля, который можно перенести";
}

/** Положить скопированный стиль на выделенные. null — всё прошло молча. */
export function pasteStyleToSelection(scene: StyleTargetScene | null, ids: string[]): string | null {
  if (!hasCopiedStyle()) return "Сначала скопируйте стиль: выделите объект и нажмите Ctrl+Alt+C";
  if (!scene || !ids.length) return "Выделите объекты, на которые положить стиль";
  const patches = ids.flatMap((id) => {
    const obj = scene.findObject(id);
    const patch = obj ? pasteStylePatch(obj) : null;
    return patch ? [{ id, patch }] : [];
  });
  if (!patches.length) return "Этим объектам такой стиль не подходит";
  void scene.commitBatchPatches(patches, `paste-style-${Date.now()}`);
  return null;
}
